import {
  Button,
  ChevronAndTitle,
  Heading,
  PillsContainer,
  ProductSuggestions,
  SearchBar,
} from '@rabbit/elements/shared-components';
import SearchGallery from '../../components/molecules/SearchGallery/SearchGallery';
import SearchSlider from '../../components/molecules/SearchSlider/SearchSlider';
import ROUTE_NAME from '../../utils/url-constants';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { SearchResults, SearchVendables } from '@rabbit/search/cherchons';
import { DTVendable } from '@rabbit/data/types';
import { RenderSearchGallery } from '../VendableSearchContainerFirestore/VendableSearchContainerFirestore';
import { useProductSearchConfig } from '../../hooks/useProductSearchConfig';
import { AppContext } from '@rabbit/app-context';

interface GroupedResults {
  [type: string]: DTVendable[];
}

interface VendableSearchContainerProps {
  // If this is defined, then the user is on the NLIC flow
  onChangeStep?: (step_in: number, data: any) => void;
}
export function VendableSearchContainer({
  onChangeStep,
}: VendableSearchContainerProps) {
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string[]>([]);
  const [results, setResults] = useState<GroupedResults>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const navigate = useNavigate();
  const { config, tenantInfo } = useContext(AppContext);

  const { shouldAutoLoadProducts, t } = useProductSearchConfig();

  const QUERY_PARAM = 'searchParams';
  const searchParams = new URLSearchParams(window.location.search);
  const brand = searchParams.get('brand');
  const defaultBrand = config?.VENDABLES?.SHOW_ONLY_BRAND_PRODUCTS
    ? tenantInfo?.name?.toLowerCase() || undefined
    : undefined;

  // If this tenant is configured to only show brand products, ensure we always have a brand in the URL
  useEffect(() => {
    if (!config?.VENDABLES?.SHOW_ONLY_BRAND_PRODUCTS) return;
    if (brand) return;
    if (!defaultBrand) return;

    const params = new URLSearchParams(window.location.search);
    params.set('brand', defaultBrand);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.VENDABLES?.SHOW_ONLY_BRAND_PRODUCTS, brand, defaultBrand, navigate]);

  const updateQueryString = (arr: string[]) => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('brand') && defaultBrand) {
      params.set('brand', defaultBrand);
    }

    if (arr?.length) {
      params.set(QUERY_PARAM, arr.join(',').toLowerCase());
    } else {
      params.delete(QUERY_PARAM);
    }

    navigate({ search: params.toString() });
  };

  const addValue = (value: string) => {
    if (!searchValue.includes(value)) {
      const newArray = [...searchValue, value];
      if (newArray?.length) updateQueryString(newArray);
      if (noResults) updateQueryString([value]);
    }
  };

  const groupResultsByCategory = (
    data: SearchResults<DTVendable>,
    property: string
  ) => {
    const groupedData = data.items.reduce((groupedData, currentItem: any) => {
      // Handle items with no category or empty category array
      const categoryValues = currentItem[property] || [];
      const propertyValues =
        categoryValues.length > 0 ? categoryValues.slice(-1)[0] : 'other';

      if (!groupedData[propertyValues]) {
        groupedData[propertyValues] = [];
      }

      groupedData[propertyValues].push(currentItem);
      return groupedData;
    }, {} as Record<string, DTVendable[]>);

    const ascendingSortedGroupedData = Object.fromEntries(
      Object.entries(groupedData).sort(([a], [b]) => a.localeCompare(b))
    ) as Record<string, DTVendable[]>;

    return ascendingSortedGroupedData;
  };

  const findResults = useCallback(
    async (searchQuery: string) => {
      const facetFilters = [
        {
          name: 'mfr',
          value: `M:${t('tenantLink')}`,
        },
      ];
      if (brand) {
        facetFilters.push({
          name: 'brand',
          value: brand!,
        });
      }
      let data = await SearchVendables(searchQuery, {
        hitsPerPage: 500,
        page: 0,
        facetFilters: facetFilters,
        //facetFilters: [
        //{ name: 'docid', value: 'sv1-c3bduni3whdq5ipy' },
        //{name: 'series', value: 'umbrella'},
        //],
      });
      // filter out unpublished items
      const publishedItems = data.items.filter(
        (item) => item.published !== false && !item.unique
      );
      data = { ...data, items: publishedItems };
      const groupedData = groupResultsByCategory(data, 'category');
      //console.log(`findResults`,{data,groupedData});

      if (Object.values(groupedData).length) {
        setResults(groupedData);
        setNoResults(false);
      } else {
        setResults({});
        setNoResults(true);
      }

      return groupedData;
    },
    [t, brand]
  );

  const handleSubmitSearch = (event?: { key: string }) => {
    if (!searchInput) return;
    if (event && event.key !== 'Enter') return;

    addValue(searchInput);
    setSearchInput('');
  };

  const handleQuickSearch = (value: string) => {
    addValue(value);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    setSearchInput(event.target.value);
  };

  const handleShowMore = async (value: string) => {
    if (!searchValue.includes(value)) {
      setSearchValue([...searchValue, value]);
      addValue(value);
    }

    const filteredResults = Object.keys(results || {}).reduce((acc, key) => {
      const group = (results || {})[key];

      const matchingItems = group.filter(
        (item) => item.series === value && item.published !== false
      );

      if (matchingItems.length > 0) {
        acc[key] = matchingItems;
      }
      return acc;
    }, {} as GroupedResults);

    setResults(filteredResults);
  };

  const handleDeleteSearchItem = (index: number, value: string) => {
    const newSearchValue = [...searchValue];
    newSearchValue.splice(index, 1);
    setSearchValue(newSearchValue);
    if (newSearchValue) updateQueryString(newSearchValue);
  };

  // Auto-load products on initial render based on configuration
  useEffect(() => {
    if (
      shouldAutoLoadProducts &&
      (!config.VENDABLES.SHOW_ONLY_BRAND_PRODUCTS || brand)
    ) {
      findResults('');
    }
  }, [shouldAutoLoadProducts, findResults, config, brand]);

  // Handle search value changes
  useEffect(() => {
    if (
      searchValue.length &&
      (!config.VENDABLES.SHOW_ONLY_BRAND_PRODUCTS || brand)
    ) {
      findResults(searchValue.join(','))
        .then((data) => console.log('success:', data))
        .catch((err) => console.log(err));
    } else if (!shouldAutoLoadProducts) {
      // Only clear results if we're not supposed to show products by default
      setResults([] as unknown as GroupedResults);
    }
  }, [searchValue, shouldAutoLoadProducts, findResults, config, brand]);

  //check if queryString was updated and then updated the search values for results
  useEffect(() => {
    const paramValues = searchParams.get(QUERY_PARAM) || '';
    if (paramValues.split(', ') !== searchValue) {
      const paramsArray = paramValues.split(',');
      if (paramsArray && paramsArray[0]?.length) setSearchValue(paramsArray);
      else setSearchValue([]);
    }
  }, [window.location.search, QUERY_PARAM]);

  return (
    <>
      <div className="mt-[42px] max-w-full">
        <SearchBar
          value={searchInput}
          handleChangeCallBack={handleChange}
          submitSearchCallback={handleSubmitSearch}
          placeholder={'Type something here'}
          allowedPattern={/^[ A-Za-z0-9._-]+$/}
        />
      </div>
      {searchValue.length > 0 && (
        <div className="mt-4">
          <PillsContainer
            values={searchValue}
            onClick={handleDeleteSearchItem}
          />
        </div>
      )}
      <div>
        <RenderSearchGallery
          results={results}
          handleQuickSearch={handleQuickSearch}
          onChangeStep={onChangeStep}
          noResults={noResults}
          searchValue={searchValue}
          handleShowMore={handleShowMore}
        />
      </div>
    </>
  );
}
export default VendableSearchContainer;
